import React, { useEffect, useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, ChevronRight, Search, Truck, Star } from 'lucide-react';
import { SERVICE_CITIES, SERVICE_PRODUCT_SLUGS, getServiceProduct, type ServiceCity } from '../data/cityMaterialContent';
// Sitemap-style index page that links to every generated City × Material
// landing page. The layout splits into a FEATURED row of 3 flagship cities
// (one per region — Pierce home, Seattle flagship, Eastside flagship)
// followed by a compact two-column county-grouped list of the rest. The
// rhythm change keeps the page from feeling like a wall of identical cards.
// Display order of the featured cities at the top of /areas. The filter
// below preserves THIS order (not the order cities appear in
// SERVICE_CITIES) so we can lead with the biggest-market cities.
const FEATURED_SLUGS = ['seattle', 'bellevue', 'tacoma'];
// Subtle zone-driven accent so customers can scan delivery zones across
// the page at a glance. Stays muted — never competes with brand-orange.
// Three delivery tiers: Zone 1 inner (+$80), Zone 2 middle (+$120),
// Zone 3 outer (+$175). Curated city.zone values (legacy 1–5) still map
// onto the correct tier's accent + fee: old 1/2 → inner, old 3 → middle,
// old 4/5 → outer.
// Zone is signalled by a small colour pill, using the SAME three colours as
// the delivery-zone map rings (data/deliveryZones.ts) so a customer who has
// seen the map recognises the tier instantly.
const ZONE_PILL: Record<1 | 2 | 3, string> = {
  1: 'bg-[#00899B]',
  2: 'bg-[#FDAC53]',
  3: 'bg-[#DD4132]'
};
const ZONE_FEE: Record<number, string> = {
  1: '+$80',
  2: '+$80',
  3: '+$120',
  4: '+$175',
  5: '+$175'
};
// Legacy curated zone (1–5) → the actual tier number shown to customers.
// Without this the UI printed "Zone 4"/"Zone 5" for a business that only has
// three zones, and the legend below listed Z1/Z2/Z3 straight off the legacy
// map — which rendered two identical +$80 entries and never showed +$175.
const ZONE_TIER: Record<number, 1 | 2 | 3> = {
  1: 1,
  2: 1,
  3: 2,
  4: 3,
  5: 3
};
const ZONE_TIERS: {
  tier: 1 | 2 | 3;
  fee: string;
  dot: string;
}[] = [{
  tier: 1,
  fee: '+$80',
  dot: 'bg-[#04A05C]'
}, {
  tier: 2,
  fee: '+$120',
  dot: 'bg-amber-400'
}, {
  tier: 3,
  fee: '+$175',
  dot: 'bg-rose-400'
}];
export function ServiceAreas() {
  const [query, setQuery] = useState('');
  // Fixed flagship trio. This deliberately ignores the region's own city list —
  // that ordering promoted small cities (e.g. Mercer Island) above Bellevue and
  // Tacoma, which are the higher-volume markets we want leading the page.
  const FEATURED_SLUGS = ['seattle', 'bellevue', 'tacoma'];
  useEffect(() => {
    const orig = document.title;
    document.title = 'Service Areas — 206Gravel Delivery to Tacoma, Seattle, Bellevue, Puyallup & more';
    return () => {
      document.title = orig;
    };
  }, []);
  const products = useMemo(() => SERVICE_PRODUCT_SLUGS.map(s => getServiceProduct(s)).filter(Boolean) as {
    product: any;
    category: any;
  }[], []);
  const matches = (city: ServiceCity) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return city.name.toLowerCase().includes(q) || city.county.toLowerCase().includes(q) || city.neighborhoods.some(n => n.toLowerCase().includes(q));
  };
  // Map over FEATURED_SLUGS (not filter on SERVICE_CITIES) so the
  // display order respects the curated lead-with list above.
  const featured = FEATURED_SLUGS.map(slug => SERVICE_CITIES.find(c => c.slug === slug)).filter(Boolean) as typeof SERVICE_CITIES;
  const featuredVisible = featured.filter(matches);
  // Non-featured grouped by county for the secondary section.
  const remainingByCounty = useMemo(() => {
    const remaining = SERVICE_CITIES.filter(c => !FEATURED_SLUGS.includes(c.slug) && matches(c));
    return {
      King: remaining.filter(c => c.county === 'King'),
      Pierce: remaining.filter(c => c.county === 'Pierce')
    };
  }, [query]);
  const totalMatches = featuredVisible.length + remainingByCounty.King.length + remainingByCounty.Pierce.length;
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-4029" data-mp="3v8qgx0c77mh5">
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-4030" data-mp="438ne7dz1f90">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-4031" data-mp="92lamt8r8oc4">
          <li data-id="element-4032" data-mp="e1ock4tfuefv">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-4033" data-mp="e14umo3sfq1u">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-4034" data-mp="ito6ngmtud4j" />
          <li className="text-stone-900 dark:text-white font-semibold" aria-current="page" data-id="element-4035" data-mp="mtyp3srt2w0a">
            Service Areas
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8" data-id="element-4036" data-mp="ruw2zt5s93vz">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-4037" data-mp="wsxpko2f3lc7">
          <div className="inline-flex items-center gap-2 mb-3" data-id="element-4038" data-mp="11wqd8sfxmfk7">
            <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-4039" data-mp="11zchoj7j0ey7" />
            <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-4040" data-mp="wyf30glq631g">
              Puget Sound · King &amp; Pierce County
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-4041" data-mp="1pl99uapmd3wg">
            Where we deliver
          </h1>
          <p className="mt-4 text-stone-600 dark:text-stone-300 max-w-3xl" data-id="element-4042" data-mp="1fvprr79qde2n">
            We deliver gravel, topsoil, bark, and drain rock across South King
            and Pierce County. Tap a city to see pricing, materials, and
            delivery details specific to your neighborhood.
          </p>
        </motion.div>

        {/* Search + zone legend */}
        <div className="mt-6 grid md:grid-cols-[1fr,auto] gap-4 items-center" data-id="element-4043" data-mp="1otcclw32v95">
          <div className="relative" data-id="element-4044" data-mp="1qwbbvf7ryq0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-4045" data-mp="6ofost4387gw" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search a city or neighborhood…" className="w-full pl-10 pr-4 py-3 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-orange" data-id="element-4046" data-mp="3o4xn8dqxo3g4" />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] font-bold text-stone-600 dark:text-stone-300" data-id="element-4047" data-mp="355s0b72nb3sw">
            <span className="uppercase tracking-wider text-stone-400 dark:text-stone-500" data-id="element-4048" data-mp="3a3tlbxjmtd70">
              Zone fees
            </span>
            {ZONE_TIERS.map(t => <span key={t.tier} className="inline-flex items-center gap-1.5" data-id="element-4049" data-mp="13ccagcedtsyz">
                <span className={`block w-2 h-2 rounded-full ${t.dot}`} data-id="element-4050" data-mp="18cgta92rizsd" />
                Z{t.tier} {t.fee}
              </span>)}
          </div>
        </div>
      </section>

      {/* Featured cities */}
      {featuredVisible.length > 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10" data-id="element-4051" data-mp="32l3u9m4ou4wi">
          <div className="flex items-center gap-2 mb-4" data-id="element-4052" data-mp="32n6sgtfzv4cb">
            <Star className="w-4 h-4 text-brand-orange" data-id="element-4053" data-mp="2xtcd227f9bj1" />
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-orange" data-id="element-4054" data-mp="3bls7wfq6638v">
              Featured cities
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4" data-id="element-4055" data-mp="37jf3pvr2mde7">
            {featuredVisible.map((city, idx) => <motion.div key={city.slug} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.05 * idx
        }} id={city.slug} className="scroll-mt-24 p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-4056" data-mp="3liwtoeg3rwp5" data-mpi={idx}>
                <div className="flex items-start justify-between gap-3 mb-4" data-id="element-4057" data-mp="10e80bpc92wg1">
                  <div data-id="element-4058" data-mp="15crn1ryo4c9g">
                    <h3 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-4059" data-mp="15c9fhz6k2bm8">
                      <Link to={`/gravel-delivery/${city.slug}`} className="hover:text-brand-orange transition-colors" data-id="element-4060" data-mp="1a4j7dkr8dlr5">
                        {city.name}
                      </Link>
                    </h3>
                    <p className="mt-1.5 flex items-center gap-2 text-[11px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider" data-id="element-4061" data-mp="hg3zrjafi5pu">
                      <span className={`px-2 py-0.5 rounded-full text-white ${ZONE_PILL[ZONE_TIER[city.zone]]}`} data-id="element-4062" data-mp="rd9tkzafgatr">
                        Zone {ZONE_TIER[city.zone]}
                      </span>
                      {city.county} County
                    </p>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-stone-100 dark:bg-white/[0.06] text-[11px] font-bold text-stone-700 dark:text-stone-200 shrink-0" data-id="element-4063" data-mp="22uhw1f1ijgva">
                    {ZONE_FEE[city.zone]}
                  </span>
                </div>

                <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed mb-4" data-id="element-4064" data-mp="3nb90hlh033xb">
                  {city.blurb}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-5" data-id="element-4065" data-mp="3ih4ryxs36nu4">
                  {city.neighborhoods.slice(0, 4).map(n => <span key={n} className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-xs font-medium text-stone-600 dark:text-stone-400" data-id="element-4066" data-mp="38lbhrl1ub6fi">
                      {n}
                    </span>)}
                  {city.neighborhoods.length > 4 && <span className="px-2 py-0.5 text-[10px] font-medium text-stone-400" data-id="element-4067" data-mp="2zlenn3wolfd2">
                      +{city.neighborhoods.length - 4} more
                    </span>}
                </div>

                <div className="grid grid-cols-2 gap-1.5 mb-4" data-id="element-4068" data-mp="xs76rq1f6pp2">
                  {products.map(({
              product
            }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="group px-3 py-2 rounded-lg bg-stone-50 dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-4069" data-mp="suypdro3ipov">
                      <div className="text-xs font-bold text-stone-900 dark:text-white truncate group-hover:text-brand-orange transition-colors" data-id="element-4070" data-mp="3tgl30m1d06ca">
                        {product.name}
                      </div>
                      <div className="text-[10px] text-stone-500 dark:text-stone-400" data-id="element-4071" data-mp="31q6qat531027">
                        ${product.pricePerYard}/yd
                      </div>
                    </Link>)}
                </div>

                <Link to={`/delivery-zones?address=${encodeURIComponent(city.geocodeHint)}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange hover:underline" data-id="element-4072" data-mp="3aj0bt8b4gvoi">
                  See {city.name} on the zone map
                  <ArrowRight className="w-3 h-3" data-id="element-4073" data-mp="2dzj6b9hw6jza" />
                </Link>
              </motion.div>)}
          </div>
        </section>}

      {/* More service areas — county-grouped */}
      {(remainingByCounty.Pierce.length > 0 || remainingByCounty.King.length > 0) && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-4074" data-mp="28nw09ieqn1ef">
          <div className="flex items-center gap-2 mb-4" data-id="element-4075" data-mp="1tyhlfuse7rrn">
            <Truck className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-4076" data-mp="1ywj6gl9dq15r" />
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400" data-id="element-4077" data-mp="1yuf2y4fe7460">
              More service areas
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-4078" data-mp="2rjvuz0z3oj1p">
            {(['King', 'Pierce'] as const).map(county => {
          const list = remainingByCounty[county];
          if (list.length === 0) return null;
          return <div key={county} data-id="element-4079" data-mp="2hl69ky7zwggp">
                  {/* County name links to the county landing page, which
                      aggregates every city and material for that county. */}
                  <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-3" data-id="element-4080" data-mp="169f8h08pen3h">
                    <Link to={`/gravel-delivery/${county.toLowerCase()}-county`} className="hover:text-brand-orange transition-colors" data-id="element-4081" data-mp="38tzre0sofld9">
                      {county} County
                    </Link>
                    <span className="ml-2 text-xs font-normal text-stone-400" data-id="element-4082" data-mp="2z34yweou7pjd">
                      {list.length} cit{list.length === 1 ? 'y' : 'ies'}
                    </span>
                  </h3>
                  <div className="space-y-3" data-id="element-4083" data-mp="2p7uzlyptpw9a">
                    {list.map(city => <div key={city.slug} id={city.slug} className="scroll-mt-24 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-4084" data-mp="2l4ou4tajc93v">
                        <div className="flex items-center justify-between gap-3 mb-2" data-id="element-4085" data-mp="2b70np63khd5v">
                          <div data-id="element-4086" data-mp="eegzsjg3heeq">
                            <p className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-4087" data-mp="eeqtoi49g5pp">
                              <Link to={`/gravel-delivery/${city.slug}`} className="hover:text-brand-orange transition-colors" data-id="element-4088" data-mp="9gfju7dytv7h">
                                {city.name}
                              </Link>
                            </p>
                            <p className="mt-1 flex items-center gap-2 text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider" data-id="element-4089" data-mp="lvx0krufgit5">
                              <span className={`px-2 py-0.5 rounded-full text-white ${ZONE_PILL[ZONE_TIER[city.zone]]}`} data-id="element-4090" data-mp="vmrn7xbl5kf4">
                                Zone {ZONE_TIER[city.zone]}
                              </span>
                              {city.neighborhoods.length}+ neighborhoods
                            </p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-[10px] font-bold text-stone-700 dark:text-stone-200 shrink-0" data-id="element-4091" data-mp="80l4oh4jcman">
                            {ZONE_FEE[city.zone]}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-id="element-4092" data-mp="1oel4ls3dsbxw">
                          {products.map(({
                    product
                  }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="inline-flex items-center px-2.5 py-1 rounded-full bg-stone-100 dark:bg-white/[0.06] text-xs font-bold text-stone-700 dark:text-stone-200 hover:bg-brand-orange/10 hover:text-brand-orange transition-colors" data-id="element-4093" data-mp="1hwhytc4jwd24">
                              {product.name}
                            </Link>)}
                        </div>
                      </div>)}
                  </div>
                </div>;
        })}
          </div>
        </section>}

      {/* Empty state */}
      {totalMatches === 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-4094" data-mp="29no2u3ne6uwo">
          <div className="p-10 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 text-center" data-id="element-4095" data-mp="29ph8pgq6lb3m">
            <p className="text-sm text-stone-700 dark:text-stone-300" data-id="element-4096" data-mp="2jm3uq8u8u878">
              No service areas match "{query}". Try a different city or
              neighborhood.
            </p>
            <button type="button" onClick={() => setQuery('')} className="mt-3 text-xs font-bold text-brand-orange hover:underline" data-id="element-4097" data-mp="378pb3q4n8fy4">
              Clear search
            </button>
          </div>
        </section>}

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-4098" data-mp="2y77v07r8ke9f">
        <div className="p-6 rounded-2xl bg-stone-900 dark:bg-black border border-stone-800 text-center" data-id="element-4099" data-mp="2jiv0lf7sr3h1">
          <h2 className="font-heading text-xl font-bold text-white" data-id="element-4100" data-mp="2oad0s11zx00y">
            Not seeing your city?
          </h2>
          <p className="mt-2 text-sm text-stone-300" data-id="element-4101" data-mp="2ajrj2foa2w4n">
            We deliver well beyond these cities. Check the zone map or call us
            for a custom quote.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3" data-id="element-4102" data-mp="20k0neanzeb2z">
            <Link to="/delivery-zones" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-4103" data-mp="25jc5bn033mm5">
              View Zone Map
              <ArrowRight className="w-3.5 h-3.5" data-id="element-4104" data-mp="1hn8serku8syi" />
            </Link>
            <a href="tel:206-650-0129" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition-colors" data-id="element-4105" data-mp="1coxiyhyggkgt">
              (206) 650-0129
            </a>
          </div>
        </div>
      </section>
    </main>;
}